import { useSearchParams, useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeftIcon } from '@radix-ui/react-icons';
import { PageHeader, Alert, AlertTitle, AlertDescription, Card, CardContent, Badge, Spinner, PostLink } from '../components/ui';
import { EvidenceDrawer, FilterBar } from '../components/composite';
import {
	useHeartbeatRunDetail,
	AccountIdentity,
	ListeningState,
	MoodTiles,
	PostReactions,
	FlaggedLane,
	StanceStrip,
	Reply,
	replyFilters,
	replySortOptions,
	STATUS_LABEL,
} from '../components/features/heartbeat';
import { ROUTE_PATTERNS, buildRoute } from '../constants/Paths';
import { formatUTCDate } from '../utils/dates';
import { HeartbeatNotesDialog, ContextReviewBanner, ReadingSelector, isEarlierReading } from '../components/features/heartbeat/HeartbeatContext';
import HeartbeatNavigation from '../components/features/heartbeat/HeartbeatNavigation';
import { reviewIsActive } from '../components/features/heartbeat/useHeartbeatContext';

/**
 * One pulse in detail - level 3.
 *
 * This content used to live on the account page and render only the newest
 * terminal run, so every earlier pulse was listed in the history and could not
 * be opened. Taking the runId from the route is the whole point: a run is
 * presented the same way whichever one you pick.
 */
export default function HeartbeatRunPage() {
	const { accountId, runId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const revision = searchParams.get('revision') || undefined;
	const page = useHeartbeatRunDetail(accountId, runId, revision);
	const [contextOpen, setContextOpen] = useState(false);
	const openContext = () => { page.closeVoices(); setContextOpen(true); };
	const setRevision = (value) => {
		page.closeVoices();
		setSearchParams((previous) => { const next = new URLSearchParams(previous); if (value) next.set('revision', value); else next.delete('revision'); return next; }, { replace: true });
	};

	const sort = searchParams.get('sort') || 'recent';
	// Functional form: building from the captured `searchParams` lets a write
	// with no render between it and this one (a FilterBar chip in the same
	// tick) be discarded. Ordinary sequential clicks re-render in between.
	const setSort = (v) => setSearchParams((prev) => {
		const next = new URLSearchParams(prev);
		next.set('sort', v);
		return next;
	}, { replace: true });

	const voicesTitle = page.voices
		? `${page.voices.title || 'Replies'} · ${page.replies.length}${page.hasMoreReplies ? '+' : ''}`
		: 'Replies';

	const run = page.run;
	const status = run?.status;
	// createdAt is the run's own "taken" date; startedAt is the fallback for any
	// run predating its addition to the run-state payload.
	const takenAt = run ? formatUTCDate(run.createdAt || run.startedAt) : null;

	// Counts come from the run itself, so the header agrees with the tiles below
	// it even while a run is still being read.
	const summary = run
		? [
			`${run.replies?.collected ?? 0} replies`,
			`${run.targetsCount ?? 0} posts`,
			run.replies?.failed ? `${run.replies.failed} unread` : null,
		].filter(Boolean).join(' · ')
		: null;

	return (
		<main className="p-4 sm:p-8 max-w-7xl mx-auto">
			<PageHeader
				title="This pulse"
				description={takenAt ? `Taken ${takenAt}` : 'Loading this pulse.'}
				actions={
					<Link
						to={buildRoute(ROUTE_PATTERNS.HEARTBEAT_ACCOUNT, { accountId })}
						className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
					>
						<ArrowLeftIcon className="h-4 w-4" />
						All pulses for this account
					</Link>
				}
			/>
			<HeartbeatNavigation />

			<AccountIdentity
				account={page.account}
				isLoading={page.accountLoading}
				currentAccountId={accountId}
				lastPulseAt={run?.createdAt || run?.startedAt}
				onContext={openContext}
			/>

			{page.runLoading && !run && (
				<Card><CardContent className="flex items-center gap-3 text-sm text-muted-foreground"><Spinner size="sm" /> Loading this pulse…</CardContent></Card>
			)}

			{!page.runLoading && !run && (
				<Alert variant="destructive">
					<AlertTitle>We couldn't find this pulse</AlertTitle>
					<AlertDescription>It may have been removed, or belong to another workspace.</AlertDescription>
				</Alert>
			)}

			{run && (
				<div className="space-y-6">
					<ReadingSelector run={run} revision={revision} onChange={setRevision} />
					{(run.incompleteEvidence || run.historicalEvidenceComplete === false) && <Alert><AlertDescription>Some original reply evidence is unavailable for this older pulse. These totals are the saved reading.</AlertDescription></Alert>}
					<div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
						<Badge variant="outline" size="sm">{STATUS_LABEL[status] || status}</Badge>
						{summary && <span>{summary}</span>}
					</div>

					{page.isActive && <ListeningState run={run} />}

					{status === 'partial' && (
						<Alert>
							<AlertTitle>Partial pulse</AlertTitle>
							<AlertDescription>
								Some posts couldn't be read this time. What we did hear is below.
							</AlertDescription>
						</Alert>
					)}

					{status === 'failed' && (
						<Alert variant="destructive">
							<AlertTitle>That pulse didn't land</AlertTitle>
							<AlertDescription>{run.error || "We couldn't read this account's replies."}</AlertDescription>
						</Alert>
					)}

					{!page.isActive && status !== 'failed' && (
						<>
							{/* Above the mood: "is anything on fire" outranks "how does the room feel". */}
							<FlaggedLane totals={run.totals} onOpen={page.openVoices} />
							<StanceStrip totals={run.totals} grid={page.grid} onOpen={page.openVoices} accountHandle={page.account?.handle} onContext={openContext} />
							<ContextReviewBanner accountId={accountId} run={run} onOpen={openContext} historical={isEarlierReading(run, revision)} />
							<MoodTiles
								mood={run.mood}
								tiles={page.tiles}
								previousTiles={page.previousTiles}
								onOpen={page.openVoices}
							/>
							<PostReactions rows={page.grid} loading={page.gridLoading} onOpen={page.openVoices} />
						</>
					)}
				</div>
			)}

			<EvidenceDrawer
				open={!!page.voices}
				onOpenChange={(o) => !o && page.closeVoices()}
				title={voicesTitle}
				description={page.voices?.text ? <PostLink url={page.voices?.url}>{page.voices.text}</PostLink> : undefined}
				filters={<FilterBar filters={replyFilters} />}
				items={page.replies}
				renderItem={(r) => <Reply r={r} accountId={accountId} runId={runId} readingRevision={run?.readingRevision} historical={isEarlierReading(run, revision)} reviewActive={reviewIsActive(run?.contextReview)} />}
				sortOptions={replySortOptions}
				sort={sort}
				onSortChange={setSort}
				loading={page.repliesLoading}
				hasMore={page.hasMoreReplies}
				onLoadMore={page.loadMoreReplies}
				emptyTitle="No replies match"
				emptyDescription="Try another standing, emotion, or post."
			/>
			<HeartbeatNotesDialog key={accountId} open={contextOpen} onOpenChange={setContextOpen} accountId={accountId} account={page.account} run={run} grid={page.grid} historical={isEarlierReading(run, revision)} />
		</main>
	);
}
